import React, { forwardRef, useImperativeHandle, useState, useRef, useMemo, useEffect, useCallback } from "react";
import PropTypes from 'prop-types'
import LeaderboardV2HeaderContainer from "../containers/leaderboardV2_header_container";
import MemberSearchContainer from "../containers/member_search_container";
import { createPortal } from "react-dom";
import { getTopPortalTarget } from "../utils/portal";
import useSheetViewport from '../hooks/useSheetViewport'
import { componentText } from '../utils/i18n_utils'

const MobileModal = forwardRef(function MobileModal({ children, onClose, type, showSearch = true }, ref) {
  const [ isOpen, setIsOpen ] = useState(false)
  const dialog = useRef(null);

  // Keeps the whole sheet inside the visible viewport, so the list scrolls
  // within what the user can see instead of running under the keyboard.
  const sheet = useSheetViewport(isOpen)

  const portalTarget = useMemo(() => getTopPortalTarget(), []);

  // Stable: MemberSearchContainer receives it as onAfterSelect, and its
  // mapDispatchToProps reads ownProps, so a new function here would rebuild
  // every member-search callback on each re-render of the sheet (the viewport
  // hook re-renders it as the keyboard or a host frame moves). onClose is read
  // through a ref so the latest one still runs.
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  const closeWithAnimation = useCallback(() => {
    setIsOpen(false);
    onCloseRef.current?.();
    const el = dialog.current;
    if (!el) return;

    if (el.classList.contains("lbv2__closing")) return;

    el.classList.add("lbv2__closing");

    const onAnimEnd = (e) => {
      if (e.target !== el) return;

      el.classList.remove("lbv2__closing");
      el.close();
      el.removeEventListener("animationend", onAnimEnd);
    };

    el.addEventListener("animationend", onAnimEnd);
  }, []);

  useImperativeHandle(ref, () => ({
    open() {
      setIsOpen(true);
      dialog.current?.showModal();
      // preventScroll: inside a foreign host's frame the anchored dialog runs
      // down to the frame's bottom edge, and a plain focus() asks the host
      // page to reveal that whole box - it scrolls to the end of the frame.
      dialog.current?.focus({ preventScroll: true })
    },
    close() {
      closeWithAnimation();
    },
  }));

  useEffect(() => {
    return () => {
      const el = dialog.current;
      if (!el) return;
      try {
        if (el.open) el.close();
      } catch {}
    };
  }, []);

  // Inside a foreign host's frame the dialog is anchored to the frame's bottom
  // edge and only its top `visibleHeight` holds the sheet (utils/viewport_utils
  // sheetFromVisibleSlice); the slide keeps one screen's distance.
  const anchored = Boolean(sheet && sheet.visibleHeight)

  return createPortal(
    <dialog
      ref={dialog}
      className={anchored ? 'lbv2__mobile-modal lbv2__mobile-modal--anchored' : 'lbv2__mobile-modal'}
      tabIndex={-1}
      style={sheet ? {
        '--lbv2-sheet-height': `${sheet.height}px`,
        '--lbv2-sheet-bottom': `${sheet.bottom}px`,
        ...(anchored ? {
          '--lbv2-sheet-visible-height': `${sheet.visibleHeight}px`,
          '--lbv2-sheet-slide': `${sheet.visibleHeight}px`,
        } : {}),
      } : undefined}
      onCancel={(e) => {
        e.preventDefault();
        closeWithAnimation();
      }}
      onClick={(e) => {
        if (e.target === dialog.current) closeWithAnimation();
      }}
    >
      <div
        className={`lbv2__mobile-modal__sheet ${type}`}
        onClick={(e) => e.stopPropagation()}
      >
        <LeaderboardV2HeaderContainer type={type}>
          <button
            type="button"
            className="lbv2__mobile-modal__close"
            aria-label={componentText('mobile_modal.close', 'Close')}
            onClick={closeWithAnimation}
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </LeaderboardV2HeaderContainer>
        {type === 'lbv2__events' && showSearch && (
          <div className="lbv2__mobile-modal__search-section">
            <MemberSearchContainer onAfterSelect={closeWithAnimation} defaultExpanded={true} />
          </div>
        )}
        {children}
      </div>
    </dialog>,
    portalTarget
  );
});

MobileModal.propTypes = {
  children: PropTypes.node,
  onClose: PropTypes.func,
  type: PropTypes.string,
  showSearch: PropTypes.bool,
}

export default MobileModal;
